import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiSearch, 
  FiBook, 
  FiMapPin, 
  FiPlus,
  FiX
} from 'react-icons/fi';
import notesApi from '../../services/notesApi';

const SidebarContainer = styled.aside`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 12px 28px rgba(28, 25, 23, 0.08);
  
  @media (max-width: 1024px) {
    position: fixed;
    top: 0;
    left: 0;
    width: 320px;
    height: 100vh;
    z-index: 1000;
    transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform 0.3s ease;
    border-radius: 0;
    border-left: none;
    border-top: none;
    border-bottom: none;
    box-shadow: 0 18px 40px rgba(28, 25, 23, 0.12);
  }
  
  @media (max-width: 480px) {
    width: 100%;
  }
  
  @media (min-width: 1280px) {
    height: calc(100vh - 2.5rem);
  }
`;

const SidebarHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid var(--border-color);
  background: var(--background-secondary);
  position: relative;
  
  @media (max-width: 640px) {
    padding: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 10px;
  }
  
  @media (min-width: 1280px) {
    padding: 20px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  display: none;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--background-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--background-secondary);
  }
  
  @media (max-width: 1024px) {
    display: flex;
  }
  
  @media (max-width: 640px) {
    top: 12px;
    right: 12px;
    width: 28px;
    height: 28px;
  }
  
  @media (max-width: 480px) {
    top: 10px;
    right: 10px;
    width: 24px;
    height: 24px;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 16px;
  
  @media (max-width: 640px) {
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 10px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  font-size: 14px;
  background: var(--background-primary);
  height: 40px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(120, 53, 15, 0.2);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
  
  @media (max-width: 640px) {
    padding: 6px 10px 6px 32px;
    font-size: 13px;
    height: 36px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 8px 6px 30px;
    font-size: 12px;
    height: 34px;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  
  @media (max-width: 640px) {
    left: 10px;
  }
  
  @media (max-width: 480px) {
    left: 8px;
  }
`;

const NewNoteButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 20px rgba(120, 53, 15, 0.18);
    background: var(--primary-dark);
  }
  
  @media (max-width: 640px) {
    padding: 8px 10px;
    font-size: 13px;
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 8px;
    font-size: 12px;
    gap: 4px;
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  
  @media (max-width: 640px) {
    padding: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 10px;
  }
  
  @media (min-width: 1280px) {
    padding: 20px;
  }
`;

const MobileOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.4);
  z-index: 999;
  display: none;
  
  @media (max-width: 1024px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
  }
`;

const Section = styled.div`
  margin-bottom: 16px;
  
  @media (max-width: 640px) {
    margin-bottom: 20px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 16px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 12px;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NoteList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NoteItem = styled(motion.div)`
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--background-primary);
  box-shadow: 0 6px 16px rgba(28, 25, 23, 0.06);
  position: relative;
  
  &:hover {
    border-color: rgba(120, 53, 15, 0.35);
    transform: translateY(-1px);
    box-shadow: 0 12px 22px rgba(28, 25, 23, 0.1);
  }
  
  @media (max-width: 480px) {
    padding: 10px;
  }
  
  &.active {
    border-color: rgba(120, 53, 15, 0.5);
    background: rgba(120, 53, 15, 0.08);
    box-shadow: 0 10px 20px rgba(120, 53, 15, 0.08);
  }

  &.active::before {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    bottom: 8px;
    width: 3px;
    border-radius: 999px;
    background: var(--primary-color);
  }
`;

const NoteTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const NoteMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--text-muted);
`;

const CourseCode = styled.span`
  background: var(--background-secondary);
  color: var(--text-secondary);
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 600;
`;

const WeekBadge = styled.span`
  background: rgba(120, 53, 15, 0.08);
  color: var(--text-secondary);
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 11px;
`;

const TimeAgo = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-muted);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: var(--text-muted);
  border: 1px dashed var(--border-color);
  border-radius: 16px;
  background: var(--background-secondary);
`;

const EmptyIcon = styled.div`
  font-size: 32px;
  margin-bottom: 8px;
  opacity: 0.5;
  color: var(--text-muted);
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: var(--text-muted);
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: var(--error-color);
`;


export default function MyNotesSidebar({ isOpen, onClose, currentNoteId, onNoteSelect }) {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchNotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await notesApi.getMyNotes({ search: searchQuery });
      if (response.success) {
        setNotes(response.items || []);
      } else {
        setError('Failed to fetch notes');
      }
    } catch (err) {
      console.error('Error fetching notes:', err);
      setError('Failed to fetch notes');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleNewNote = () => {
    navigate('/student/notes/new');
    onClose?.();
  };

  const handleNoteClick = (note) => {
    navigate(`/student/notes/${note.id}`);
    onNoteSelect?.(note);
    onClose?.();
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const groupNotesByCourse = (notes) => {
    const grouped = {};
    notes.forEach(note => {
      const hasCourse = Boolean(note.course_id);
      const courseName = note.course_name || 'Other';
      const courseCode = note.course_code || 'Other';
      const key = hasCourse ? `${note.course_id}-${courseName}` : 'no-course';
      if (!grouped[key]) {
        grouped[key] = {
          course: { id: note.course_id, name: courseName, code: courseCode },
          notes: []
        };
      }
      grouped[key].notes.push(note);
    });
    return Object.values(grouped);
  };

  const pinnedNotes = notes.filter(note => note.is_pinned);
  const groupedNotes = groupNotesByCourse(notes.filter(note => !note.is_pinned));

  const renderNoteItem = (note) => (
    <NoteItem
      key={note.id}
      className={currentNoteId === note.id ? 'active' : ''}
      onClick={() => handleNoteClick(note)}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <NoteTitle>
        {Boolean(note.is_pinned) && <FiMapPin size={12} color="var(--primary-color)" />}
        {note.title || 'Untitled note'}
      </NoteTitle>
      <NoteMeta>
        <CourseCode>{note.course_code || 'Other'}</CourseCode>
        <WeekBadge>{note.week_number ? `W${note.week_number}` : 'No week'}</WeekBadge>
      </NoteMeta>
      <TimeAgo>{formatTimeAgo(note.updated_at)}</TimeAgo>
    </NoteItem>
  );

  return (
    <>
      <MobileOverlay $isOpen={isOpen} onClick={onClose} />
      <SidebarContainer $isOpen={isOpen}>
        <SidebarHeader>
          <CloseButton onClick={onClose}>
            <FiX size={16} />
          </CloseButton>
          <SearchContainer>
            <SearchIcon>
              <FiSearch size={16} />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchContainer>
          <NewNoteButton onClick={handleNewNote}>
            <FiPlus size={16} />
            New Note
          </NewNoteButton>
        </SidebarHeader>

        <SidebarContent>
          {loading ? (
            <LoadingState>
              <div>Loading notes...</div>
            </LoadingState>
          ) : error ? (
            <ErrorState>
              <div>{error}</div>
            </ErrorState>
          ) : notes.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <FiBook />
              </EmptyIcon>
              <div>No notes found</div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                {searchQuery ? 'Try a different search term' : 'Create your first note'}
              </div>
            </EmptyState>
          ) : (
            <>
              {pinnedNotes.length > 0 && (
                <Section>
                  <SectionTitle>
                    <FiMapPin size={12} />
                    Pinned
                  </SectionTitle>
                  <NoteList>
                    <AnimatePresence>
                      {pinnedNotes.map(renderNoteItem)}
                    </AnimatePresence>
                  </NoteList>
                </Section>
              )}

              {groupedNotes.map(group => (
                  <Section key={group.course.id ? `${group.course.id}-${group.course.name}` : 'no-course'}>
                    <SectionTitle>
                      <FiBook size={12} />
                      {group.course.name || 'No course'}
                    </SectionTitle>
                  <NoteList>
                    <AnimatePresence>
                      {group.notes.map(renderNoteItem)}
                    </AnimatePresence>
                  </NoteList>
                </Section>
              ))}
            </>
          )}
        </SidebarContent>
      </SidebarContainer>
    </>
  );
}
